import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { Modal, Space, Button, message } from 'antd';
import { RedoOutlined } from '@ant-design/icons'
import { useOAProcessModalContext } from '@/contexts/OAProcessModalContext';
import { useAccess } from '@umijs/max';
import { getOaUrlPath } from '@/utils/utils';
import TeamOALoginManager from '@/utils/TeamOALoginManager';
import QdLoading from '@/components/Common/QdLoading';

// iframe路由配置
const IFRAME_BASE_URL = getOaUrlPath();

// Modal 模式类型
type OAModalMode = 'process' | 'list' | 'saasProcess' | 'saasList';
/**
 * OA 流程提交路由配置
 */
const PROCESS_ROUTES = (mode: OAModalMode) => {
  if (['saasProcess', 'saasList'].includes(mode)) {
    return [
      { key: 'gradient', label: '梯度优惠', path: '/process/create/10', access: 'saas-oa-process-gradient' },
      { key: 'refund', label: '其他退款', path: '/process/create/30', access: 'saas-oa-process-refund' },
      { key: 'cancel', label: '取消订单', path: '/process/handleCreate/1', access: 'saas-oa-process-cancel' },
      { key: 'logistics', label: '物流核实', path: '/process/verifyCreate/3', access: 'saas-oa-process-logistics' },
      { key: 'orderVerify', label: '订单核实', path: '/process/verifyCreate/4', access: 'saas-oa-process-verify' },
      { key: 'orderResend', label: '订单重发', path: '/process/handleCreate/6', access: 'saas-oa-process-resend' },
      // 订单修改
      { key: 'orderModify', label: '订单修改', path: '/process/handleCreate/7', access: 'saas-oa-process-modify' },
    ];
  }
  return [
    { key: 'gradient', label: '梯度优惠', path: '/process/create/10', access: 'oa-process-gradient' },
    { key: 'refund', label: '其他退款', path: '/process/create/30', access: 'oa-process-refund' },
    { key: 'cancel', label: '取消订单', path: '/process/handleCreate/1', access: 'oa-process-cancel' },
    { key: 'logistics', label: '物流核实', path: '/process/verifyCreate/3', access: 'oa-process-logistics' },
    { key: 'orderVerify', label: '订单核实', path: '/process/verifyCreate/4', access: 'oa-process-verify' },
    { key: 'orderResend', label: '订单重发', path: '/process/handleCreate/6', access: 'oa-process-resend' },
    // 订单修改
    { key: 'orderModify', label: '订单修改', path: '/process/handleCreate/7', access: 'oa-process-modify' },
  ];
}

/**
 * OA 流程列表路由配置
 */
const LIST_ROUTES = (mode: OAModalMode) => {
  if (['saasProcess', 'saasList'].includes(mode)) {
    return [
      { key: 'refundlist', label: '退费流程', path: '/process/list', access: 'saas-oa-process-list-refund' },
      { key: 'cancellist', label: '取消订单', path: '/process/cancelOrderList', access: 'saas-oa-process-list-cancel' },
      { key: 'urgentlist', label: '加急订单', path: '/process/urgentOrderList', access: 'saas-oa-process-list-urgent' },
      { key: 'logisticslist', label: '物流核实', path: '/process/verifylist', access: 'saas-oa-process-list-logistics' },
      { key: 'orderVerifylist', label: '订单核实', path: '/process/Orderverifylist', access: 'saas-oa-process-list-verify' },
      { key: 'orderResendlist', label: '订单重发', path: '/process/orderRetryList', access: 'saas-oa-process-list-resend' },
      // 订单修改
      { key: 'orderModifylist', label: '订单修改', path: '/process/orderModifyList', access: 'saas-oa-process-list-modify' },
    ];
  }
  return [
    { key: 'refundlist', label: '退费流程', path: '/process/list', access: 'oa-process-list-refund' },
    { key: 'cancellist', label: '取消订单', path: '/process/cancelOrderList', access: 'oa-process-list-cancel' },
    { key: 'urgentlist', label: '加急订单', path: '/process/urgentOrderList', access: 'oa-process-list-urgent' },
    { key: 'logisticslist', label: '物流核实', path: '/process/verifyList', access: 'oa-process-list-logistics' },
    { key: 'orderVerifylist', label: '订单核实', path: '/process/Orderverifylist', access: 'oa-process-list-verify' },
    { key: 'orderResendlist', label: '订单重发', path: '/process/orderRetryList', access: 'oa-process-list-resend' },
    // 订单修改
    { key: 'orderModifylist', label: '订单修改', path: '/process/orderModifyList', access: 'oa-process-list-modify' },
  ];
}

// Modal 配置
const MODAL_CONFIG = {
  process: {
    title: 'OA流程提交',
    routes: PROCESS_ROUTES('process'),
    warningKey: 'oa_process_modal_warning',
  },
  list: {
    title: 'OA流程列表',
    routes: LIST_ROUTES('list'),
    warningKey: 'oa_process_modal_warning',
  },
  saasProcess: {
    title: 'OA流程提交',
    routes: PROCESS_ROUTES('saasProcess'),
    warningKey: 'oa_process_modal_warning',
  },
  saasList: {
    title: 'OA流程列表',
    routes: LIST_ROUTES('saasList'),
    warningKey: 'oa_process_modal_warning',
  },
};

/**
 * OA 流程弹窗组件 Props
 *
 * @property mode - 弹窗模式：'process' 提交流程 | 'list' 查看流程列表
 * @property visible - 弹窗是否可见
 * @property emailDetail - 邮件详情数据（包含 numbers 订单编号数组）
 * @property visibleRouteKeys - 需要显示的路由 key 数组，如果不传则显示所有路由
 */
interface OAProcessModalProps {
  mode: OAModalMode;
  visible: boolean;
  emailDetail?: any;
  visibleRouteKeys?: string[];
}

const OAProcessModal: React.FC<OAProcessModalProps> = ({ mode, visible, emailDetail = {}, visibleRouteKeys }) => {
  const { closeModal } = useOAProcessModalContext();
  const access = useAccess();
  const { id = 0 } = emailDetail;
  // 获取当前模式的配置
  const config = MODAL_CONFIG[mode];
  const IFRAME_ROUTES = config.routes;
  // 登录管理器实例
  const loginManager = useRef(TeamOALoginManager.getInstance()).current;

  // 登录请求中状态
  const [isRequestingLogin, setIsRequestingLogin] = useState(false);

  // 根据权限和 visibleRouteKeys 过滤需要显示的路由
  const filteredRoutes = React.useMemo(() => {
    let routes = IFRAME_ROUTES;

    // 根据 visibleRouteKeys 过滤
    if (visibleRouteKeys && visibleRouteKeys.length > 0) {
      routes = routes.filter(route => visibleRouteKeys.includes(route.key));
    }

    // 根据权限过滤
    routes = routes.filter(route => {
      // 如果路由配置了 access，检查权限
      if (route.access) {
        return (access as any)[route.access] === true;
      }
      return true;
    });

    return routes;
  }, [visibleRouteKeys, IFRAME_ROUTES, access]);

  const { numbers = [] } = emailDetail;
  const [activeTab, setActiveTab] = useState(() => {
    // 默认选中第一个可见路由
    return filteredRoutes.length > 0 ? filteredRoutes[0].key : '';
  });
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // iframe是否登录成功的状态
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  // 是否正在检查 iframe 的登录状态
  const [isCheckingLogin, setIsCheckingLogin] = useState(true);
  // 登录状态检查超时计时器
  const checkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 清除登录检查超时计时器
   */
  const clearCheckingTimeout = useCallback(() => {
    if (checkingTimeoutRef.current) {
      clearTimeout(checkingTimeoutRef.current);
      checkingTimeoutRef.current = null;
    }
  }, []);

  /**
   * 开始检查登录状态（设置超时）
   */
  const startCheckingLogin = useCallback(() => {
    setIsCheckingLogin(true);
    // 清除之前的超时计时器
    clearCheckingTimeout();
    // 设置新的超时计时器：5秒后如果还没收到登录状态消息，认为未登录
    checkingTimeoutRef.current = setTimeout(() => {
      setIsCheckingLogin(false);
    }, 5000);
  }, [clearCheckingTimeout]);

  /**
   * 结束检查登录状态
   */
  const stopCheckingLogin = useCallback(() => {
    clearCheckingTimeout();
    setIsCheckingLogin(false);
  }, [clearCheckingTimeout]);

  //  修改 relVisible 逻辑：支持登录引导和缓存状态
  const relVisible = useCallback(() => {
    if (filteredRoutes.length === 0) {
      return visible; // 直接显示，内部会显示暂无权限页面
    }

    // 如果已登录，直接显示
    if (visible && isLoginSuccess) {
      return true;
    }

    // 如果未登录但有缓存的登录信息，显示 Modal（内部会自动同步）
    if (visible && !isLoginSuccess && loginManager.hasLoginInfo()) {
      return true;
    }

    // 如果未登录且无缓存，显示 Modal（内部会显示登录引导）
    if (visible && !isLoginSuccess) {
      return true;
    }

    return visible;
  }, [visible, isLoginSuccess, filteredRoutes.length, loginManager]);

  /**
   * 同步登录信息到 iframe
   */
  const syncLoginInfoToIframe = useCallback(() => {
    if (!iframeLoaded || !iframeRef.current?.contentWindow) {
      return;
    }

    const storage = loginManager.getStorage();
    if (Object.keys(storage).length === 0) {
      return;
    }

    // 发送完整的 localStorage 数据
    iframeRef.current.contentWindow.postMessage({
      type: 'SYNC_LOGIN_TOKEN',
      data: {
        storage,
        timestamp: Date.now()
      }
    }, IFRAME_BASE_URL);
  }, [iframeLoaded, loginManager]);

  /**
   * 处理登录请求
   */
  const handleRequestLogin = useCallback(async () => {
    setIsRequestingLogin(true);

    try {
      const success = await loginManager.requestLogin();
      if (success) {
        startCheckingLogin();
        // 登录成功，同步到 iframe（需要等待 iframe 加载完成）
        if (iframeLoaded) {
          syncLoginInfoToIframe();
        }
      }
    } catch (error) {
      message.error('登录失败，请重试');
    } finally {
      setIsRequestingLogin(false);
    }
  }, [loginManager, syncLoginInfoToIframe, iframeLoaded, startCheckingLogin]);

  // 当登录成功（有缓存信息）且 iframe 加载完成时，自动同步
  useEffect(() => {
    if (loginManager.hasLoginInfo() && iframeLoaded && !isLoginSuccess) {
      startCheckingLogin();
      syncLoginInfoToIframe();
    }
  }, [loginManager, iframeLoaded, isLoginSuccess, syncLoginInfoToIframe, startCheckingLogin]);

  // 监听全局登录状态变化（其他实例登录成功时）
  useEffect(() => {
    const unsubscribe = loginManager.onLoginStateChange(() => {
      // 如果当前实例还未登录成功，且 iframe 已加载
      if (!isLoginSuccess && iframeLoaded) {
        startCheckingLogin();
        syncLoginInfoToIframe();
      }
    });

    // 组件卸载时注销监听器
    return () => {
      unsubscribe();
    };
  }, [loginManager, isLoginSuccess, iframeLoaded, syncLoginInfoToIframe, startCheckingLogin]);

  // 当 numbers 变化时，默认选中第一个订单编号
  useEffect(() => {
    const orderNumber = numbers.length > 0 ? numbers[0] : '';
    setSelectedOrderNumber(orderNumber);
    const currentRoute = filteredRoutes.find(r => r.key === activeTab);
    if (currentRoute && iframeLoaded) {
      sendRouteChangeMessage(currentRoute, orderNumber, { reset: true });
    }
  }, [numbers, emailDetail]);

  // 当 visibleRouteKeys 变化时，更新 activeTab
  useEffect(() => {
    if (filteredRoutes.length > 0) {
      // 如果当前选中的 tab 不在可见路由中，则切换到第一个可见路由
      const isCurrentTabVisible = filteredRoutes.some(route => route.key === activeTab);
      if (!isCurrentTabVisible) {
        setActiveTab(filteredRoutes[0].key);
      }
    }
  }, [filteredRoutes, activeTab]);

  /**
   * 发送路由变更消息到 iframe
   */
  const sendRouteChangeMessage = useCallback((route: { key: string; label: string; path: string }, orderNumber?: string, options?: { reset?: boolean }) => {
    if (!iframeRef.current?.contentWindow) return;

    let query: Record<string, any> = {
      emailId: id,
    };
    if (orderNumber) {
      query.orderNumber = orderNumber;
    }
    query = { ...query, ...options };

    iframeRef.current.contentWindow.postMessage({
      type: 'ROUTE_CHANGE',
      data: {
        path: route.path,
        query,
        state: {
          tabKey: route.key,
          tabLabel: route.label,
          timestamp: Date.now()
        }
      }
    }, IFRAME_BASE_URL);
  }, [emailDetail]);

  /**
   * iframe加载完成后：优先同步登录信息，然后发送路由跳转
   */
  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);

    if (filteredRoutes.length === 0) return;

    // 优先检查是否有缓存的登录信息并同步
    if (loginManager.hasLoginInfo()) {
      startCheckingLogin();
      syncLoginInfoToIframe();
    } else {
      // 没有缓存信息，直接结束检查，显示登录引导界面
      setIsCheckingLogin(false);
    }

    // 延迟发送路由跳转（给登录同步一些时间）
    setTimeout(() => {
      const currentRoute = filteredRoutes.find(r => r.key === activeTab);
      if (currentRoute) {
        sendRouteChangeMessage(currentRoute, selectedOrderNumber, { reset: true });
      }
    }, 1000);
  }, [filteredRoutes, loginManager, syncLoginInfoToIframe, activeTab, selectedOrderNumber, startCheckingLogin, sendRouteChangeMessage]);

  /**
   * Tab切换时通过消息通知iframe进行路由跳转
   */
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const route = filteredRoutes.find(r => r.key === key);
    if (route && iframeLoaded) {
      sendRouteChangeMessage(route, selectedOrderNumber);
    }
  };

  /**
   * 刷新当前iframe页面 - 重新定位到当前activeTab和选中的订单号
   */
  const handleRefreshIframe = useCallback(() => {
    const currentRoute = filteredRoutes.find(r => r.key === activeTab);
    if (currentRoute) {
      sendRouteChangeMessage(currentRoute, selectedOrderNumber, { reset: true });
    }
  }, [filteredRoutes, activeTab, selectedOrderNumber, sendRouteChangeMessage]);

  // 监听来自iframe的消息（增加重试同步逻辑）
  useEffect(() => {
    if (filteredRoutes.length === 0) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'LOGIN_SUCCESS') {
        setIsLoginSuccess(true);
        stopCheckingLogin();
      }
      // 接受缓存过期信号
      if (event.data.type === 'CACHE_EXPIRED') {
        setIsLoginSuccess(false);
        stopCheckingLogin();
        loginManager.clearStorage();
      }

      if (event.data.type === 'LOGIN_ERROR') {
        setIsLoginSuccess(false);
        stopCheckingLogin();

        // 如果有缓存的登录信息，重试同步
        if (loginManager.hasLoginInfo() && iframeLoaded) {
          startCheckingLogin();
          setTimeout(() => {
            syncLoginInfoToIframe();
          }, 1000);
        }
      }

    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [filteredRoutes.length, loginManager, iframeLoaded, syncLoginInfoToIframe, stopCheckingLogin, startCheckingLogin]);

  //  渲染订单编号选择器
  const renderOrderNumberSelector = useMemo(() => {
    return numbers && numbers.length > 0 ? (
      <div className="qd-mb-3">
        <Space wrap>
          {numbers.map((orderNumber: string) => (
            <Button key={orderNumber} type={selectedOrderNumber === orderNumber ? 'primary' : 'default'} size="small" onClick={() => {
              setSelectedOrderNumber(orderNumber);
              const currentRoute = filteredRoutes.find(r => r.key === activeTab);
              if (currentRoute) {
                sendRouteChangeMessage(currentRoute, orderNumber);
              }
            }}>
              {orderNumber}
            </Button>
          ))}
        </Space>
      </div>
    ) : null;
  }, [numbers, selectedOrderNumber, filteredRoutes, activeTab, sendRouteChangeMessage]);

  // 渲染iframe
  const renderIframe = useMemo(() => {
    return (
      <iframe
        ref={iframeRef}
        src={IFRAME_BASE_URL}
        className="qd-w-full qd-h-full qd-border-none qd-block"
        title={filteredRoutes.find(r => r.key === activeTab)?.label}
        onLoad={handleIframeLoad}
      />
    );
  }, [iframeRef, IFRAME_BASE_URL]);
  // 组件卸载时清理超时计时器
  useEffect(() => {
    return () => {
      clearCheckingTimeout();
    };
  }, [clearCheckingTimeout]);

  // iframe 弹窗
  return (
      <Modal
        title={
          <div className="qd-flex qd-items-center qd-justify-between qd-pr-10">
            <span>{config.title}</span>
            <Button
              type="text"
              size="small"
              icon={<RedoOutlined />}
              onClick={handleRefreshIframe}
              title="刷新页面"
            />
          </div>
        }
        open={relVisible()}
        onCancel={() => closeModal(mode)}
        footer={null}
        width={1200}
        centered
        destroyOnClose={false}
        forceRender={true}
        // 不显示遮罩层和动画
        transitionName=""
        maskTransitionName=""
        styles={{
          body: { padding: '12px' },
        }}
      >
        {/* 权限检查：如果没有可访问的路由，显示暂无权限页面 */}
        {filteredRoutes.length === 0 ? (
          <div className="qd-flex qd-items-center qd-justify-center qd-h-[calc(80vh-20px)] qd-flex-col qd-gap-4">
            <div className="qd-text-gray-400 qd-text-6xl">🔒</div>
            <div className="qd-text-gray-600 qd-text-lg">暂无权限</div>
            <div className="qd-text-gray-400 qd-text-sm">您暂无访问此功能的权限，请联系管理员</div>
            <Button onClick={() => closeModal(mode)} type="primary">
              关闭
            </Button>
          </div>
        ) : (
          <div className="qd-relative qd-h-[calc(80vh-20px)]">
            {/*  状态 1：正在检查登录状态（首次加载） */}
            {isCheckingLogin && (
              <div className="qd-absolute qd-inset-0 qd-flex qd-items-center qd-justify-center qd-flex-col qd-gap-4 qd-bg-white qd-z-10">
                <QdLoading loading={true} size={56}/>
                <div className="qd-text-gray-600 qd-text-base">正在检查登录状态...</div>
                <div className="qd-text-gray-400 qd-text-sm">请稍候</div>
              </div>
            )}

            {/*  状态 2：检查完成，未登录且无缓存，显示登录引导 */}
            {!isCheckingLogin && !isLoginSuccess && !loginManager.hasLoginInfo() && (
              <div className="qd-absolute qd-inset-0 qd-flex qd-items-center qd-justify-center qd-flex-col qd-gap-4 qd-bg-white qd-z-10">
                <div className="qd-text-gray-400 qd-text-6xl">🔐</div>
                <div className="qd-text-gray-600 qd-text-lg">需要登录 TeamOA</div>
                <div className="qd-text-gray-400 qd-text-sm qd-text-center" style={{ maxWidth: '400px' }}>
                  由于浏览器安全限制，嵌入页面无法直接访问登录信息。
                  <br />
                  请在弹出窗口中登录 TeamOA，登录信息将在所有窗口间共享。
                </div>
                <Space>
                  <Button
                    type="primary"
                    onClick={handleRequestLogin}
                    loading={isRequestingLogin}
                    size="large"
                  >
                    {isRequestingLogin ? '登录中...' : '点击登录 TeamOA'}
                  </Button>
                  <Button onClick={() => closeModal(mode)} size="large">
                    取消
                  </Button>
                </Space>
              </div>
            )}

            {/*  状态 3：有缓存信息但 iframe 还未登录成功，显示同步中 */}
            {!isCheckingLogin && !isLoginSuccess && loginManager.hasLoginInfo() && (
              <div className="qd-absolute qd-inset-0 qd-flex qd-items-center qd-justify-center qd-flex-col qd-gap-4 qd-bg-white qd-z-10">
                <QdLoading loading={true} size={56}/>
                <div className="qd-text-gray-600 qd-text-base">正在同步登录信息...</div>
                <div className="qd-text-gray-400 qd-text-sm">首次加载可能需要几秒钟</div>
              </div>
            )}

            {/*  iframe 容器：总是渲染，根据登录状态决定是否显示 */}
            <div className={`qd-h-full ${!isLoginSuccess ? 'qd-opacity-0' : ''}`}>
              {/* 订单编号选择器 */}
              {isLoginSuccess && renderOrderNumberSelector}

              <div className={`qd-flex ${isLoginSuccess && numbers && numbers.length > 0 ? 'qd-h-[calc(80vh-60px)]' : 'qd-h-full'}`}>
                {/* 左侧Tab导航 - 只在登录成功时显示 */}
                {isLoginSuccess && (
                  <div className="qd-w-[100px] qd-border-r qd-border-gray-200 qd-pr-2">
                    {filteredRoutes.map(route => (
                      <div
                        key={route.key}
                        onClick={() => handleTabChange(route.key)}
                        style={activeTab === route.key ? {
                          color: '#5f49e5'
                        } : {}}
                        className={`
                          qd-px-2 qd-py-1 qd-cursor-pointer qd-rounded qd-mb-0.5 qd-transition-all qd-duration-300
                          ${activeTab === route.key
                            ? 'qd-font-medium'
                            : 'qd-bg-transparent qd-text-black hover:qd-bg-[rgba(95,73,229,0.05)]'
                          }
                        `}
                      >
                        {route.label}
                      </div>
                    ))}
                  </div>
                )}

                {/* 右侧iframe内容区 GPU加速 */}
                <div className={`qd-flex-1 qd-relative ${isLoginSuccess ? 'qd-ml-2' : ''}`} style={{
                  transform: 'translateZ(0)',
                  transformStyle: 'preserve-3d',
                  willChange: 'transform',
                  transformOrigin: 'center center',
                }}>
                  {renderIframe}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
  );
};

export default memo(OAProcessModal);
